import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import tasksSlice from "../../../store/slices/TasksSlice";
import getIcon from "../../../utils/Icon";
import Avatar from "../../avatar/Avatar";
import avatarImage from '../../../assets/avatar/avatart-male.png';
import PersonAddIcon from '../../icons/PersonAddIcon';
import YoutubeIcon from '../../icons/YoutubeIcon';
import XIcon from '../../icons/XIcon';
import TelegramIcon from '../../icons/TelegramIcon';
import DiscordIcon from '../../icons/DiscordIcon';
import InstagramIcon from '../../icons/InstagramIcon';
import VkIcon from '../../icons/VkIcon';
import LinkedInIcon from '../../icons/LinkedInIcon';
import DexScreenerIcon from '../../icons/DexScreenerIcon';
import ThreadsIcon from '../../icons/ThreadsIcon';
import RedditIcon from '../../icons/RedditIcon';
import DexToolsIcon from '../../icons/DexToolsIcon';
import TwitchIcon from '../../icons/TwitchIcon';
import TikTokIcon from '../../icons/TikTokIcon';
import PinterestIcon from '../../icons/PinterestIcon';
import {
    FollowChannelIcon,
    JoinChannelIcon,
    ReactPostIcon,
    CheckOutIcon,
    TakeLookIcon,
    CommentIcon,
    ShowLoveIcon,
    JumpInIcon,
    RetweetIcon,
    ShareIcon,
    WatchVideoIcon,
    WatchStreamIcon,
    ExploreIcon,
    GoGoIcon,
    LetsGoIcon,
} from '../../icons/tasks/TaskTypeIcons';
import { addRecentLink, TASK_SOCIAL_LINK_RECENT_KEY } from '../../../utils/keyboardRecentLinks';
import TaskApi from "../../../api/taskApi";
import { usePromoGuide } from '../PromoGuideContext';
import { isDesktopPlatform } from '../../Keyboard';

// Тултип со ссылкой при клике на аватар
const LinkTooltip = ({ link, show }) => (
    <div
        className={`absolute -bottom-8 left-0 w-[250px] rounded-lg h-6 bg-[#2373BE] z-10 transition-all duration-300 ease-out ${
            show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none'
        }`}
    >
        <div className="w-full h-full relative">
            <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-3' />
            <div className="w-full h-full flex items-center p-2 text-white text-[11px] truncate">{link}</div>
        </div>
    </div>
);

const Tooltip = ({ children, text, show, onClick, multiline = false, placement = 'left' }) => {
    const isBottom = placement === 'bottom';

    return (
        <div 
            className="relative inline-block"
            onClick={onClick}
        >
            {children}
            {show && (
                <div className={`absolute z-50 ${
                    isBottom
                        ? 'top-full -right-1 mt-2'
                        : 'right-full top-1/2 -translate-y-1/2 mr-2'
                }`}>
                    <div className={`bg-[#2B8FF4] text-white text-xs px-2 py-1 rounded ${
                        multiline ? 'w-[300px] whitespace-pre-line leading-[15px]' : 'whitespace-nowrap'
                    }`}>
                        {text}
                    </div>
                    <div className={`absolute w-0 h-0 ${
                        isBottom
                            ? 'right-2 top-[-6px] border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-[#2B8FF4]'
                            : 'left-full top-1/2 -translate-y-1/2 border-l-4 border-l-[#2B8FF4] border-t-2 border-t-transparent border-b-2 border-b-transparent'
                    }`} />
                </div>
            )}
        </div>
    );
};

const Snackbar = ({ message, type, isVisible, onClose, isAnimated }) => {
    if (!isVisible) return null;

    const bgColor = type === 'error' ? 'bg-[#fb2c36]' : 'bg-[#00c950]';
    const borderColor = type === 'error' ? 'border-red-500' : 'border-green-500';

    return (
        <div className={`fixed top-0 left-1/2 transform -translate-x-1/2 z-50 
            ${bgColor} ${borderColor} border text-white px-4 pt-24 pb-3 shadow-xl 
            transition-all duration-500 ease-out min-w-[100%]
            ${isAnimated ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-full opacity-0 '}
        `}>
            <div className="flex items-center justify-between ">
                <div className="flex items-center justify-center space-x-2 flex-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'}`}></div>
                    <span className="text-xs font-medium text-white">{message}</span>
                </div>
                <button
                    onClick={onClose}
                    className="text-white/70 hover:text-white transition-colors duration-200 flex-shrink-0 ml-3"
                    aria-label="Close notification"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div className="absolute bottom-0 left-0 h-0.5 bg-white/20 rounded-b-lg overflow-hidden">
                <div className={`h-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'} transition-all duration-4000 ease-linear`}></div>
            </div>
        </div>
    );
};

const TASK_TYPES = [
    'Follow Channel',
    'Join Channel',
    'React to Post',
    'Check This Out',
    'Take a Look',
    'Comment',
    'Show Some Love',
    'Jump In',
    'Retweet',
    'Share',
    'Watch Video',
    'Tune In'
];

const TASK_TYPE_META = [
    { value: 'Follow Channel', label: 'Follow', Icon: FollowChannelIcon },
    { value: 'Join Channel', label: 'Join', Icon: JoinChannelIcon },
    { value: 'React to Post', label: 'React', Icon: ReactPostIcon },
    { value: 'Check This Out', label: 'Check', Icon: CheckOutIcon },
    { value: 'Take a Look', label: 'Look', Icon: TakeLookIcon },
    { value: 'Comment', label: 'Comment', Icon: CommentIcon },
    { value: 'Show Some Love', label: 'Love', Icon: ShowLoveIcon },
    { value: 'Jump In', label: 'Jump In', Icon: JumpInIcon },
    { value: 'Retweet', label: 'Retweet', Icon: RetweetIcon },
    { value: 'Share', label: 'Share', Icon: ShareIcon },
    { value: 'Watch Video', label: 'Watch', Icon: WatchVideoIcon },
    { value: 'Watch Stream', label: 'Tune In', Icon: WatchStreamIcon }
];
const TASKS_PER_PAGE = 12; // 3 rows × 3 cols

function stripTaskEmoji(text) {
    return String(text || '')
        .replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '')
        .trim();
}

function getLabelByTaskType(type) {
    const lower = stripTaskEmoji(type).toLowerCase();
    if (lower.includes('telegram')) return 'telegram';
    if (lower.includes('channel')) return 'telegram';
    if (lower.includes('retweet') || lower.includes('x') || lower.includes('twitter')) return 'twitter';
    return 'link';
}

function resolveTaskType(text) {
    const clean = stripTaskEmoji(text);
    const found = TASK_TYPES.find((t) => t.toLowerCase() === clean.toLowerCase());
    return found || clean || 'Follow Channel';
}

/** Stable component — must not be declared inside TasksList or the input remounts on each keystroke. */
function AddTaskItem({
    taskType,
    setTaskType,
    taskUrl,
    setTaskUrl,
    isAtTaskLimit,
    isEditing,
    onSubmit,
    onCancelEdit,
    showAddTasksGuide,
    completeAddTasks,
}) {
    const [dropdownPage, setDropdownPage] = useState(0);
    const [dragOffsetPx, setDragOffsetPx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const swipeStartXRef = useRef(null);
    const sliderViewportRef = useRef(null);
    const SWIPE_THRESHOLD = 36;
    const totalPages = Math.ceil(TASK_TYPE_META.length / TASKS_PER_PAGE);
    const pageSlides = Array.from({ length: totalPages }).map((_, pageIdx) =>
        TASK_TYPE_META.slice(pageIdx * TASKS_PER_PAGE, pageIdx * TASKS_PER_PAGE + TASKS_PER_PAGE)
    );
    const canSubmit = Boolean(taskUrl.trim()) && (isEditing || !isAtTaskLimit);

    useEffect(() => {
        const idx = TASK_TYPE_META.findIndex((t) => t.value === taskType);
        if (idx >= 0) {
            setDropdownPage(Math.floor(idx / TASKS_PER_PAGE));
        }
    }, [taskType]);

    const handleSwipeStart = (e) => {
        swipeStartXRef.current = e.touches?.[0]?.clientX ?? null;
        setIsDragging(true);
        setDragOffsetPx(0);
    };

    const handleSwipeMove = (e) => {
        if (swipeStartXRef.current == null) return;
        const x = e.touches?.[0]?.clientX;
        if (typeof x !== 'number') return;

        const viewportWidth = sliderViewportRef.current?.clientWidth || 1;
        let deltaX = x - swipeStartXRef.current;

        // light resistance at edges
        if ((dropdownPage === 0 && deltaX > 0) || (dropdownPage === totalPages - 1 && deltaX < 0)) {
            deltaX *= 0.35;
        }
        const maxDrag = viewportWidth * 0.9;
        setDragOffsetPx(Math.max(-maxDrag, Math.min(maxDrag, deltaX)));
    };

    const handleSwipeEnd = (e) => {
        if (swipeStartXRef.current == null) return;
        const endX = e.changedTouches?.[0]?.clientX;
        if (typeof endX !== 'number') {
            swipeStartXRef.current = null;
            setIsDragging(false);
            setDragOffsetPx(0);
            return;
        }

        const deltaX = endX - swipeStartXRef.current;
        swipeStartXRef.current = null;
        setIsDragging(false);
        setDragOffsetPx(0);

        if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
        if (deltaX < 0) {
            setDropdownPage((prev) => Math.min(prev + 1, totalPages - 1));
        } else {
            setDropdownPage((prev) => Math.max(prev - 1, 0));
        }
    };

    return (
        <div data-guide="addTasks" className="w-full flex flex-col items-center gap-3 px-4 pt-3 pb-2 ">
            <div
                className="w-full rounded-[12px] border"
                style={{
                    background: '#000000',
                    borderColor: '#2C2C2C',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                }}
            >
                <div
                    className="min-h-[220px] flex flex-col px-2 pt-2 pb-1 gap-1"
                    ref={sliderViewportRef}
                    onTouchStart={handleSwipeStart}
                    onTouchMove={handleSwipeMove}
                    onTouchEnd={handleSwipeEnd}
                >
                    <div className="overflow-hidden">
                        <div
                            className="flex"
                            style={{
                                transform: `translateX(calc(${-dropdownPage * 100}% + ${dragOffsetPx}px))`,
                                transition: isDragging ? 'none' : 'transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)',
                            }}
                        >
                            {pageSlides.map((slide, slideIdx) => (
                                <div key={slideIdx} className="w-full flex-shrink-0">
                                    <div className="grid grid-cols-4 grid-rows-3 gap-2">
                                        {slide.map(({ value, label, Icon }) => {
                                            const selected = value === taskType;
                                            return (
                                                <button
                                                    key={value}
                                                    type="button"
                                                    onClick={() => setTaskType(value)}
                                                    className="h-[60px] rounded-[12px] border flex flex-col items-center justify-center gap-1
                                                        active:scale-[0.98] transition-transform"
                                                    style={{
                                                        borderColor: selected ? '#EAEAEA' : 'rgba(234, 234, 234,0.30)',
                                                        background: selected ? 'rgba(234, 234, 234,0.05)' : 'rgba(234, 234, 234,0.05)',
                                                    }}
                                                >
                                                    <div className={selected ? 'text-white' : 'text-white/15'}>
                                                        <Icon />
                                                    </div>
                                                    <span className={`text-[11px] leading-[13px] text-center px-1 ${selected ? 'text-white' : 'text-white/30'}`}>
                                                        {label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="w-full flex justify-center items-center ">
                        {Array.from({ length: totalPages }).map((_, pageIdx) => {
                            const active = pageIdx === dropdownPage;
                            return (
                                <button
                                    key={pageIdx}
                                    type="button"
                                    onClick={() => setDropdownPage(pageIdx)}
                                    aria-label={`Page ${pageIdx + 1}`}
                                    className="flex items-center justify-center p-1"
                                >
                                    <span
                                        className="rounded-full transition-all duration-200"
                                        style={{
                                            width: active ? 8 : 6,
                                            height: active ? 8 : 6,
                                            background: active ? '#EAEAEA' : 'rgba(234, 234, 234,0.35)',
                                        }}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div
                className="w-full h-[44px] rounded-[10px] flex items-center px-4 gap-3"
                style={{
                    background: '#1E1E1E',
                    border: '1px solid #2C2C2C',
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="#959595" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="#959595" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                    type="text"
                    value={taskUrl}
                    onChange={e => setTaskUrl(e.target.value)}
                    placeholder="Paste social media link"
                    data-kb-recent-key={TASK_SOCIAL_LINK_RECENT_KEY}
                    onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
                    onBlur={() => {
                        if (showAddTasksGuide && isDesktopPlatform) completeAddTasks();
                    }}
                    className="flex-1 min-w-0 bg-transparent text-white text-[14px] outline-none border-none placeholder-[#959595] text-center"
                />
                {taskUrl.trim() && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTaskUrl('');
                        }}
                        className="flex-shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center
                            active:scale-95 transition-transform"
                        style={{ background: 'rgba(234, 234, 234,0.1)' }}
                        aria-label="Clear link"
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="#C0C0C0" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                    </button>
                )}
            </div>

            <button
                type="button"
                onClick={onSubmit}
                disabled={!canSubmit}
                className="w-full h-[46px] rounded-[10px] flex items-center justify-center
                    active:scale-[0.98] transition-all duration-100 border disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                style={{
                    background: canSubmit ? '#207FE0' : 'transparent',
                    borderColor: '#207FE0',
                    transition: 'background 0.2s, transform 0.1s',
                }}
            >
                <span className="text-white text-[15px] font-semibold">
                    {isEditing ? 'Update Task' : 'Add Task'}
                </span>
            </button>
            {isEditing && (
                <button
                    type="button"
                    onClick={onCancelEdit}
                    className="w-full h-[36px] rounded-[10px] flex items-center justify-center
                        active:scale-[0.98] transition-transform text-[#959595] text-[13px]"
                >
                    Cancel
                </button>
            )}
        </div>
    );
}

export default function TasksList({ maxTasks = 10 }) {
    const dispatch = useDispatch();
    const tasks = useSelector(state => state.tasks?.items || []);
    const disabledDefaultIds = useSelector(state => state.tasks?.disabledDefaultTaskIds || []);
    const isAtTaskLimit = tasks.length >= maxTasks;
    const { step, completeAddTasks } = usePromoGuide();
    const showAddTasksGuide = step === 'addTasks';
    const [taskType, setTaskType] = useState('Follow Channel');
    const [taskUrl, setTaskUrl] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [showDefaultTooltip, setShowDefaultTooltip] = useState(false);
    const [showSocialTooltip, setShowSocialTooltip] = useState(false);
    const [snackbar, setSnackbar] = useState({ isVisible: false, message: '', type: 'error' });
    const [snackbarAnimation, setSnackbarAnimation] = useState(false);
    const editPanelRef = useRef(null);

    const [defaultTasks, setDefaultTasks] = useState([]);

    useEffect(() => {
        if (editingIndex == null) return;
        const id = requestAnimationFrame(() => {
            editPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
        return () => cancelAnimationFrame(id);
    }, [editingIndex]);

    useEffect(() => {
        TaskApi.getDefaultTasks()
            .then(data => setDefaultTasks(data || []))
            .catch(() => setDefaultTasks([]));
    }, []);

    const getDefaultTaskIcon = (task) => {
        if (task.icon === 'wallet') return getIcon('wallet');
        if (task.icon === 'twitter') return getIcon('octo');
        if (task.icon === 'telegram') return getIcon('octo');
        return getIcon('octo');
    };

    const getDefaultTaskLabel = (task) => {
        if (task.icon === 'twitter') return 'twitter';
        if (task.icon === 'telegram') return 'telegram';
        return task.icon || '';
    };

    const DefaultTasksRow = () => {
        if (!defaultTasks.length) {
            return (
                <div className=" flex flex-row items-center py-[12.5px] justify-center gap-[54px] border-b border-[#353232]">
                    <div className="flex flex-col items-center gap-[4px]">
                        <div className="w-[40px] h-[40px] rounded-full flex items-center justify-center" style={{ background: '#D3D3D3' }}>
                            <PersonAddIcon width={24} height={24} />
                        </div>
                        <div className="h-[26px] flex items-center">
                            <span className='text-[11px] text-white'>Invite friend</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-row items-center py-[12.5px] justify-center gap-[30px] flex-wrap border-b border-[#353232] px-2">
                {defaultTasks.map(task => {
                    const isInvite = task.type === 'invite_friend' || task.icon === 'invite';
                    const optional = task.is_locked === false;
                    const disabled = disabledDefaultIds.includes(Number(task.id));
                    return (
                        <div
                            key={task.id}
                            className={`flex flex-col items-center gap-[4px] ${disabled ? 'opacity-35' : ''} ${optional ? 'cursor-pointer active:scale-95 transition' : ''}`}
                            style={{ maxWidth: 70 }}
                            onClick={optional ? () => dispatch(tasksSlice.actions.toggleDefaultTask(task.id)) : undefined}
                        >
                            {isInvite ? (
                                <div className="w-[40px] h-[40px] rounded-full flex items-center justify-center" style={{ background: '#D3D3D3' }}>
                                    <PersonAddIcon width={24} height={24} />
                                </div>
                            ) : (
                                <Avatar height={40} width={40} icon={getDefaultTaskIcon(task)} label={getDefaultTaskLabel(task)} />
                            )}
                            <div className="h-[26px] flex flex-col items-center justify-center">
                                <span className='text-[11px] text-white text-center leading-[13px]'>{task.title}</span>
                                {optional && (
                                    <span className={`text-[9px] leading-[11px] ${disabled ? 'text-[#959595]' : 'text-emerald-400'}`}>
                                        {disabled ? 'off' : 'on'}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const normalizeTaskLink = (url) => {
        const trimmed = String(url || '').trim();
        if (!trimmed) return '';
        return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    };

    const closeSnackbar = () => {
        setSnackbarAnimation(false);
        setTimeout(() => {
            setSnackbar(prev => ({ ...prev, isVisible: false }));
        }, 300);
    };

    const showSnackbar = (message, type = 'error') => {
        setSnackbar({ isVisible: true, message, type });
        setTimeout(() => setSnackbarAnimation(true), 100);
    };

    useEffect(() => {
        if (!snackbar.isVisible) return undefined;
        const timer = setTimeout(closeSnackbar, 4000);
        return () => clearTimeout(timer);
    }, [snackbar.isVisible]);

    const isYoutubeLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'youtube.com'
                || hostname === 'www.youtube.com'
                || hostname === 'm.youtube.com'
                || hostname === 'youtu.be';
        } catch {
            return /youtube\.com|youtu\.be/i.test(url);
        }
    };

    const isXLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'x.com'
                || hostname === 'www.x.com'
                || hostname === 'twitter.com'
                || hostname === 'www.twitter.com'
                || hostname === 'mobile.twitter.com'
                || hostname === 't.co'
                || hostname === 'www.t.co';
        } catch {
            return /(^|\/\/)(www\.)?(x\.com|twitter\.com|t\.co)/i.test(url);
        }
    };

    const isTelegramLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 't.me'
                || hostname === 'www.t.me'
                || hostname === 'telegram.me'
                || hostname === 'www.telegram.me'
                || hostname === 'telegram.org'
                || hostname === 'www.telegram.org';
        } catch {
            return /(^|\/\/)(www\.)?(t\.me|telegram\.(me|org))/i.test(url);
        }
    };

    const isDiscordLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'discord.com'
                || hostname === 'www.discord.com'
                || hostname === 'discord.gg'
                || hostname === 'www.discord.gg'
                || hostname === 'discordapp.com'
                || hostname === 'www.discordapp.com';
        } catch {
            return /(^|\/\/)(www\.)?(discord\.(com|gg)|discordapp\.com)/i.test(url);
        }
    };

    const isInstagramLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'instagram.com'
                || hostname === 'www.instagram.com'
                || hostname.endsWith('.instagram.com');
        } catch {
            return /(^|\/\/)(www\.)?instagram\.com/i.test(url);
        }
    };

    const isVkLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'vk.com'
                || hostname === 'www.vk.com'
                || hostname === 'vk.ru'
                || hostname === 'www.vk.ru'
                || hostname.endsWith('.vk.com')
                || hostname.endsWith('.vk.ru');
        } catch {
            return /(^|\/\/)(www\.)?vk\.(com|ru)/i.test(url);
        }
    };

    const isLinkedInLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'linkedin.com'
                || hostname === 'www.linkedin.com'
                || hostname.endsWith('.linkedin.com');
        } catch {
            return /(^|\/\/)(www\.)?linkedin\.com/i.test(url);
        }
    };

    const isDexScreenerLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'dexscreener.com'
                || hostname === 'www.dexscreener.com'
                || hostname.endsWith('.dexscreener.com');
        } catch {
            return /(^|\/\/)(www\.)?dexscreener\.com/i.test(url);
        }
    };

    const isThreadsLink = (url) => {
        if (!url) return false;
        const normalized = String(url).trim();
        if (/threads\.(net|com)/i.test(normalized)) return true;
        try {
            const hostname = new URL(normalizeTaskLink(normalized)).hostname.toLowerCase();
            return hostname === 'threads.net'
                || hostname === 'www.threads.net'
                || hostname.endsWith('.threads.net')
                || hostname === 'threads.com'
                || hostname === 'www.threads.com'
                || hostname.endsWith('.threads.com');
        } catch {
            return false;
        }
    };

    const isRedditLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'reddit.com'
                || hostname === 'www.reddit.com'
                || hostname === 'old.reddit.com'
                || hostname === 'redd.it'
                || hostname.endsWith('.reddit.com');
        } catch {
            return /(^|\/\/)(www\.|old\.)?reddit\.com|redd\.it/i.test(url);
        }
    };

    const isDexToolsLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'dextools.io'
                || hostname === 'www.dextools.io'
                || hostname.endsWith('.dextools.io');
        } catch {
            return /(^|\/\/)(www\.)?dextools\.io/i.test(url);
        }
    };

    const isTwitchLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.toLowerCase();
            return hostname === 'twitch.tv'
                || hostname === 'www.twitch.tv'
                || hostname === 'm.twitch.tv'
                || hostname.endsWith('.twitch.tv');
        } catch {
            return /(^|\/\/)(www\.|m\.)?twitch\.tv/i.test(url);
        }
    };

    const isTiktokLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(normalizeTaskLink(url)).hostname.toLowerCase();
            return hostname === 'tiktok.com'
                || hostname.endsWith('.tiktok.com');
        } catch {
            return /(^|\/\/)([\w-]+\.)*tiktok\.com/i.test(url);
        }
    };

    const isPinterestLink = (url) => {
        if (!url) return false;
        try {
            const hostname = new URL(normalizeTaskLink(url)).hostname.toLowerCase();
            return hostname === 'pinterest.com'
                || hostname === 'www.pinterest.com'
                || hostname.endsWith('.pinterest.com')
                || hostname === 'pin.it'
                || hostname === 'www.pin.it';
        } catch {
            return /(^|\/\/)([\w-]+\.)*pinterest\.com|pin\.it/i.test(url);
        }
    };

    const getTaskPlatform = (url) => {
        if (!url) return null;
        if (isYoutubeLink(url)) return 'youtube';
        if (isXLink(url)) return 'x';
        if (isTelegramLink(url)) return 'telegram';
        if (isDiscordLink(url)) return 'discord';
        if (isInstagramLink(url)) return 'instagram';
        if (isVkLink(url)) return 'vk';
        if (isLinkedInLink(url)) return 'linkedin';
        if (isDexScreenerLink(url)) return 'dexscreener';
        if (isThreadsLink(url)) return 'threads';
        if (isRedditLink(url)) return 'reddit';
        if (isDexToolsLink(url)) return 'dextools';
        if (isTwitchLink(url)) return 'twitch';
        if (isTiktokLink(url)) return 'tiktok';
        if (isPinterestLink(url)) return 'pinterest';
        return null;
    };

    const isKnownPlatformLink = (url) => getTaskPlatform(url) != null;

    const isValidTaskLink = (url) => isKnownPlatformLink(url);

    const getFaviconUrl = (url) => {
        if (!url || isKnownPlatformLink(url)) return null;
        try {
            const domain = new URL(normalizeTaskLink(url)).hostname;
            return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
        } catch {
            return null;
        }
    };

    const PlatformBadge = () => (
        <img
            src={getIcon('octo')}
            alt=""
            className="w-3.5 h-3.5 absolute top-0 -right-0 z-10 rounded-full object-contain"
        />
    );

    const renderTaskPlatformAvatar = (platform, { avatarRef, handleAvatarClick, link, showLinkTooltip }) => {
        const wrap = (iconNode, className, style) => (
            <div
                ref={avatarRef}
                className="relative w-[40px] h-[40px] flex-shrink-0"
                onClick={handleAvatarClick}
            >
                <div className={className} style={style}>
                    {iconNode}
                </div>
                <PlatformBadge />
                {link && <LinkTooltip link={link} show={showLinkTooltip} />}
            </div>
        );

        switch (platform) {
            case 'youtube':
                return wrap(<YoutubeIcon width={22} height={16} />, 'w-full h-full rounded-full bg-[#CD201F] flex items-center justify-center');
            case 'x':
                return wrap(<XIcon width={18} height={18} />, 'w-full h-full rounded-full bg-black flex items-center justify-center');
            case 'telegram':
                return wrap(<TelegramIcon width={24} height={19} />, 'w-full h-full rounded-full bg-[#00A2E1] flex items-center justify-center');
            case 'discord':
                return wrap(<DiscordIcon width={22} height={16} />, 'w-full h-full rounded-full bg-[#8C9EFF] flex items-center justify-center');
            case 'instagram':
                return wrap(
                    <InstagramIcon width={20} height={20} />,
                    'w-full h-full rounded-full flex items-center justify-center',
                    { background: 'linear-gradient(to top, #FFD879 10%, #FCCB76 15%, #F5AA6C 26%, #E9755E 40%, #E45F58 45%, #D22A9C 68%, #6968DF 100%)' }
                );
            case 'vk':
                return wrap(<VkIcon width={22} height={14} />, 'w-full h-full rounded-full bg-[#0077FF] flex items-center justify-center');
            case 'linkedin':
                return wrap(<LinkedInIcon width={30} height={30} />, 'w-full h-full rounded-full flex items-center justify-center');
            case 'dexscreener':
                return wrap(<DexScreenerIcon width={20} height={24} />, 'w-full h-full rounded-full bg-[#0D1117] flex items-center justify-center');
            case 'threads':
                return wrap(<ThreadsIcon width={22} height={22} fill="#EAEAEA" />, 'w-full h-full rounded-full bg-black flex items-center justify-center');
            case 'reddit':
                return wrap(<RedditIcon width={21} height={20} />, 'w-full h-full rounded-full bg-[#FF4500] flex items-center justify-center');
            case 'dextools':
                return wrap(<DexToolsIcon width={21} height={24} />, 'w-full h-full rounded-full bg-white flex items-center justify-center');
            case 'twitch':
                return wrap(<TwitchIcon width={24} height={24} />, 'w-full h-full rounded-full bg-[#9146FF] flex items-center justify-center');
            case 'tiktok':
                return wrap(<TikTokIcon width={20} height={20} fill="#EAEAEA" />, 'w-full h-full rounded-full bg-black flex items-center justify-center');
            case 'pinterest':
                return wrap(<PinterestIcon width={20} height={20} fill="#EAEAEA" />, 'w-full h-full rounded-full bg-[#E60023] flex items-center justify-center');
            default:
                return null;
        }
    };

    // Item теперь используется для отображения тасков из массива tasks
    const Item = ({ text, label, isComplated, link, index, isEditing, onEdit, onDelete }) => {
        const platform = getTaskPlatform(link);
        const faviconUrl = platform ? null : getFaviconUrl(link);
        const [showLinkTooltip, setShowLinkTooltip] = useState(false);
        const avatarRef = useRef(null);

        useEffect(() => {
            if (!showLinkTooltip) return;
            const handler = (e) => {
                if (avatarRef.current && !avatarRef.current.contains(e.target)) {
                    setShowLinkTooltip(false);
                }
            };
            document.addEventListener('mousedown', handler);
            return () => document.removeEventListener('mousedown', handler);
        }, [showLinkTooltip]);

        const handleAvatarClick = (e) => {
            e.stopPropagation();
            if (link) setShowLinkTooltip((prev) => !prev);
        };

        const handleEdit = () => {
            if (onEdit) onEdit(index);
        };

        const handleDelete = (e) => {
            e.stopPropagation();
            if (onDelete) {
                onDelete(index);
            }
        };

        return (
            <div
                className={`flex flex-row w-full justify-between items-center h-[75px] rounded-[10px] px-1 ${
                    isEditing ? '' : ''
                }`}
                style={{ userSelect: 'none' }}
                tabIndex={0}
                role="button"
                onClick={handleEdit}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleEdit();
                    }
                }}
            >
                <div className="flex flex-row items-center gap-[12px] flex-1 cursor-pointer min-w-0">
                    <span className="text-[15px] text-[#959595] font-medium w-[18px] flex-shrink-0 text-center">
                        {index + 1}
                    </span>
                    {platform
                        ? renderTaskPlatformAvatar(platform, {
                            avatarRef,
                            handleAvatarClick,
                            link,
                            showLinkTooltip,
                        })
                        : faviconUrl ? (
                        <div
                            ref={avatarRef}
                            className="relative w-[40px] h-[40px] rounded-full flex-shrink-0"
                            onClick={handleAvatarClick}
                        >
                            <img 
                                src={faviconUrl} 
                                alt="site icon" 
                                className="w-full h-full object-cover rounded-full"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<img src="${avatarImage}" class="w-[40px] h-[40px] rounded-full" />`;
                                }}
                            />
                            <PlatformBadge />
                            {link && <LinkTooltip link={link} show={showLinkTooltip} />}
                        </div>
                    ) : (
                        <div
                            ref={avatarRef}
                            className="relative w-[40px] h-[40px] flex-shrink-0"
                            onClick={handleAvatarClick}
                        >
                            <Avatar height={40} width={40} icon={avatarImage} label="octo" />
                            {link && <LinkTooltip link={link} show={showLinkTooltip} />}
                        </div>
                    )}
                    <span className="text-[15px] text-white font-semi">{text}</span>
                </div>
                <div className="flex items-center gap-2">
                    {isComplated && <img width={15} height={10} src={getIcon('checked')} alt="Completed" />}
                    <button
                        onClick={handleDelete}
                        className="w-[24px] h-[24px] flex items-center justify-center rounded-full bg-red-500/20 hover:bg-red-500/40 transition-colors"
                        title="Delete task"
                    >
                        <span className="text-red-400 text-[14px] font-bold">×</span>
                    </button>
                </div>
            </div>
        );
    }

    const resetTaskForm = () => {
        setEditingIndex(null);
        setTaskUrl('');
        setTaskType('Follow Channel');
    };

    const handleEditTask = (index) => {
        const task = tasks[index];
        if (!task) return;
        if (editingIndex === index) {
            resetTaskForm();
            return;
        }
        setEditingIndex(index);
        setTaskType(resolveTaskType(task.text));
        setTaskUrl(task.link || '');
    };

    const handleSubmitTask = () => {
        const trimmedUrl = taskUrl.trim();
        if (!trimmedUrl) return;
        if (!isValidTaskLink(trimmedUrl)) {
            showSnackbar('Only Telegram, X, YouTube, Discord, TikTok, Instagram, Reddit, VK, Threads, LinkedIn, Twitch, DexScreener, DexTools, Pinterest links are allowed.', 'error');
            setTaskUrl('');
            return;
        }
        const normalizedUrl = normalizeTaskLink(trimmedUrl);
        const nextTask = {
            text: stripTaskEmoji(taskType),
            label: getLabelByTaskType(taskType),
            link: normalizedUrl,
            isComplated: false,
        };

        if (editingIndex != null) {
            dispatch(tasksSlice.actions.updateTask({
                index: editingIndex,
                task: {
                    ...nextTask,
                    isComplated: Boolean(tasks[editingIndex]?.isComplated),
                },
            }));
            addRecentLink(TASK_SOCIAL_LINK_RECENT_KEY, normalizedUrl);
            resetTaskForm();
            return;
        }

        if (isAtTaskLimit) return;
        dispatch(tasksSlice.actions.addTask(nextTask));
        addRecentLink(TASK_SOCIAL_LINK_RECENT_KEY, normalizedUrl);
        setTaskUrl('');
    };

    const handleDeleteTask = (index) => {
        dispatch(tasksSlice.actions.removeTask(index));
        if (editingIndex == null) return;
        if (editingIndex === index) {
            resetTaskForm();
        } else if (editingIndex > index) {
            setEditingIndex(editingIndex - 1);
        }
    };

    return (
        <div className='w-full flex flex-col'>
            <Snackbar
                message={snackbar.message}
                type={snackbar.type}
                isVisible={snackbar.isVisible}
                isAnimated={snackbarAnimation}
                onClose={closeSnackbar}
            />
            <div className='flex flex-row items-center justify-between border-b border-[#353232] px-[16px] py-[5px] pb-[10px]'>
                <span className="text-[11px] text-[#959595] ">default</span>
                <Tooltip 
                    text="Default Tasks(Locked)"
                    show={showDefaultTooltip}
                    onClick={() => setShowDefaultTooltip(!showDefaultTooltip)}
                >
                    <img src={getIcon('inc')} alt="faq" className="w-[15px] h-[15px]" />
                </Tooltip>
            </div>
            <DefaultTasksRow />
            <div className='flex flex-row justify-between border-b border-[#353232] px-[16px] py-[10px]' >
                <span className='text-[11px] text-[#959595] '>social media</span>
                <Tooltip 
                    text={(
                        <div className="flex flex-col gap-2 px-1 py-1.5">
                            <span className="text-white text-[13px] font-semibold">Your Tasks(links):</span>
                            <div className="flex flex-row flex-wrap items-center gap-2">
                                <div className="w-[26px] h-[26px] rounded-full bg-[#00A2E1] flex items-center justify-center flex-shrink-0">
                                    <TelegramIcon width={16} height={13} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-black flex items-center justify-center flex-shrink-0">
                                    <ThreadsIcon width={15} height={15} fill="#EAEAEA" />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#E60023] flex items-center justify-center flex-shrink-0">
                                    <PinterestIcon width={14} height={14} fill="#EAEAEA" />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-black flex items-center justify-center flex-shrink-0">
                                    <XIcon width={12} height={12} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#CD201F] flex items-center justify-center flex-shrink-0">
                                    <YoutubeIcon width={15} height={11} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#9146FF] flex items-center justify-center flex-shrink-0">
                                    <TwitchIcon width={16} height={16} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                    <DexToolsIcon width={14} height={16} />
                                </div>
                                <div
                                    className="w-[26px] h-[26px] rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ background: 'linear-gradient(to top, #FFD879 10%, #FCCB76 15%, #F5AA6C 26%, #E9755E 40%, #E45F58 45%, #D22A9C 68%, #6968DF 100%)' }}
                                >
                                    <InstagramIcon width={14} height={14} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#0077FF] flex items-center justify-center flex-shrink-0">
                                    <VkIcon width={15} height={10} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-black flex items-center justify-center flex-shrink-0">
                                    <TikTokIcon width={14} height={14} fill="#EAEAEA" />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#FF4500] flex items-center justify-center flex-shrink-0">
                                    <RedditIcon width={15} height={14} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#8C9EFF] flex items-center justify-center flex-shrink-0">
                                    <DiscordIcon width={15} height={11} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full  flex items-center justify-center flex-shrink-0">
                                    <LinkedInIcon width={20} height={20} />
                                </div>
                                <div className="w-[26px] h-[26px] rounded-full bg-[#0D1117] flex items-center justify-center flex-shrink-0">
                                    <DexScreenerIcon width={13} height={16} />
                                </div>
                            </div>
                        </div>
                    )}
                    multiline
                    placement="bottom"
                    show={showSocialTooltip}
                    onClick={() => setShowSocialTooltip(!showSocialTooltip)}
                >
                    <img src={getIcon('inc')} alt="faq" className="w-[15px] h-[15px]" />
                </Tooltip>
            </div>
            <div className="w-full pb-4 ">
                <div className=" flex w-full flex-col items-start justify-center">
                    {tasks.length > 0 && (
                        <div className="flex w-full flex-col ">
                            {tasks.map((task, idx) => (
                                <div key={idx} className="w-full">
                                    <div className={`flex w-full flex-row justify-center items-center ${idx > 0 ? 'border-t border-[#353232]' : ''}`}>
                                        <div className=" px-3 w-full">
                                            <Item
                                                text={stripTaskEmoji(task.text)}
                                                label={task.label}
                                                link={task.link}
                                                isComplated={task.isComplated}
                                                index={idx}
                                                isEditing={editingIndex === idx}
                                                onEdit={handleEditTask}
                                                onDelete={handleDeleteTask}
                                            />
                                        </div>
                                    </div>
                                    {editingIndex === idx && (
                                        <div ref={editPanelRef} className="border-t border-[#353232]">
                                            <AddTaskItem
                                                taskType={taskType}
                                                setTaskType={setTaskType}
                                                taskUrl={taskUrl}
                                                setTaskUrl={setTaskUrl}
                                                isAtTaskLimit={isAtTaskLimit}
                                                isEditing
                                                onSubmit={handleSubmitTask}
                                                onCancelEdit={resetTaskForm}
                                                showAddTasksGuide={showAddTasksGuide}
                                                completeAddTasks={completeAddTasks}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {isAtTaskLimit && editingIndex == null ? (
                        <div className="w-full flex flex-col items-center justify-center gap-[4px] pt-6 pb-2 px-4">
                            <span className="text-white text-[16px] font-semibold text-center">
                                All Tasks Completed!
                            </span>
                            <span className="text-[#959595] text-[13px] text-center">
                                Tap a task to edit, or finish your Promo.
                            </span>
                        </div>
                    ) : editingIndex == null ? (
                        <AddTaskItem
                            taskType={taskType}
                            setTaskType={setTaskType}
                            taskUrl={taskUrl}
                            setTaskUrl={setTaskUrl}
                            isAtTaskLimit={isAtTaskLimit}
                            isEditing={false}
                            onSubmit={handleSubmitTask}
                            onCancelEdit={resetTaskForm}
                            showAddTasksGuide={showAddTasksGuide}
                            completeAddTasks={completeAddTasks}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}